import React from "react";
import Footer from "../components/landing/Footer";
import TopNavBar from "../components/nav/TopNavBar";

const TermsOfService: React.FC = () => {
  return (
    <>
      <TopNavBar />
      <div className="w-full relative bg-white overflow-hidden flex flex-col items-start justify-start leading-[normal] tracking-[normal] p-6">
        <h1 className="text-2xl font-bold mb-4">Terms of Service</h1>
        <p className="mb-4">Last updated: 2026/03/08</p>

        <p className="mb-4">
          Welcome to YouWoAI ("we," "us," or "our"). By accessing or using our
          application, website, and services (collectively, the "Service"), you
          agree to be bound by these Terms of Service ("Terms"). If you do not
          agree to these Terms, please do not use the Service.
        </p>

        <h2 className="text-xl font-semibold mb-2">1. Eligibility</h2>
        <p className="mb-4">
          You must be at least 13 years of age (or the minimum age required in
          your jurisdiction) to use the Service. By using the Service, you
          represent that you meet this requirement. If you are under 18, you must
          have the consent of a parent or legal guardian.
        </p>

        <h2 className="text-xl font-semibold mb-2">2. Account Registration</h2>
        <p className="mb-4">
          To access certain features of the Service, you may be required to
          create an account. You agree to provide accurate, current, and complete
          information and to keep your account credentials secure. You are
          responsible for all activity that occurs under your account.
        </p>

        <h2 className="text-xl font-semibold mb-2">3. Use of the Service</h2>
        <p className="mb-4">
          YouWoAI provides AI-powered document processing, note-taking, and
          content generation tools. You may use the Service for lawful purposes
          only. You agree not to:
        </p>
        <ul className="list-disc pl-5 mb-4">
          <li>Use the Service to violate any applicable law or regulation</li>
          <li>Upload content that infringes the intellectual property rights of others</li>
          <li>Attempt to gain unauthorized access to the Service or its systems</li>
          <li>Interfere with or disrupt the integrity or performance of the Service</li>
          <li>Use the Service to distribute malware, spam, or harmful content</li>
          <li>Reverse-engineer, decompile, or disassemble any part of the Service</li>
        </ul>

        <h2 className="text-xl font-semibold mb-2">4. User Content</h2>
        <p className="mb-4">
          You retain ownership of any content you upload, create, or submit
          through the Service ("User Content"). By submitting User Content, you
          grant us a limited, non-exclusive, worldwide license to process, store,
          and display your content solely for the purpose of providing and
          improving the Service. We do not claim ownership of your User Content.
        </p>

        <h2 className="text-xl font-semibold mb-2">5. Third-Party Integrations</h2>
        <p className="mb-4">
          The Service may integrate with third-party services such as Google
          (Gmail, Google Drive), Microsoft (Outlook, OneNote), Notion, and Canvas
          LMS. When you connect a third-party account, you authorize us to access
          and retrieve data from that service on your behalf in accordance with
          the permissions you grant. Your use of third-party services is also
          subject to their respective terms and privacy policies.
        </p>

        <h2 className="text-xl font-semibold mb-2">6. AI-Generated Content</h2>
        <p className="mb-4">
          The Service uses artificial intelligence to generate summaries,
          quizzes, flashcards, and other study materials from your content.
          AI-generated output is provided "as is" and may contain inaccuracies.
          You are responsible for reviewing and verifying any AI-generated
          content before relying on it.
        </p>

        <h2 className="text-xl font-semibold mb-2">7. Subscriptions and Payments</h2>
        <p className="mb-4">
          Certain features of the Service require a paid subscription. By
          subscribing, you agree to pay the applicable fees. Subscriptions
          automatically renew unless cancelled before the end of the current
          billing period. Refunds are handled in accordance with the policies of
          the applicable payment platform (Apple App Store, Google Play, or
          Stripe).
        </p>

        <h2 className="text-xl font-semibold mb-2">8. Intellectual Property</h2>
        <p className="mb-4">
          The Service, including its design, code, features, and branding, is
          owned by YouWoAI and protected by intellectual property laws. These
          Terms do not grant you any right to use our trademarks, logos, or other
          brand elements without prior written consent.
        </p>

        <h2 className="text-xl font-semibold mb-2">9. Termination</h2>
        <p className="mb-4">
          We may suspend or terminate your access to the Service at any time if
          you violate these Terms or for any other reason at our discretion. You
          may delete your account at any time through the app settings. Upon
          termination, your right to use the Service ceases, and we may delete
          your data in accordance with our Privacy Policy.
        </p>

        <h2 className="text-xl font-semibold mb-2">10. Disclaimers</h2>
        <p className="mb-4">
          The Service is provided on an "as is" and "as available" basis without
          warranties of any kind, whether express or implied. We do not warrant
          that the Service will be uninterrupted, error-free, or secure. We
          disclaim all warranties, including implied warranties of
          merchantability, fitness for a particular purpose, and
          non-infringement.
        </p>

        <h2 className="text-xl font-semibold mb-2">11. Limitation of Liability</h2>
        <p className="mb-4">
          To the maximum extent permitted by law, YouWoAI shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages
          arising out of or related to your use of the Service. Our total
          liability shall not exceed the amount you paid us in the twelve (12)
          months preceding the claim.
        </p>

        <h2 className="text-xl font-semibold mb-2">12. Indemnification</h2>
        <p className="mb-4">
          You agree to indemnify and hold harmless YouWoAI and its officers,
          directors, employees, and agents from any claims, damages, or expenses
          arising from your use of the Service, your User Content, or your
          violation of these Terms.
        </p>

        <h2 className="text-xl font-semibold mb-2">13. Changes to These Terms</h2>
        <p className="mb-4">
          We may update these Terms from time to time. If we make material
          changes, we will notify you through the Service or by other means. Your
          continued use of the Service after such changes constitutes your
          acceptance of the updated Terms.
        </p>

        <h2 className="text-xl font-semibold mb-2">14. Governing Law</h2>
        <p className="mb-4">
          These Terms shall be governed by and construed in accordance with the
          laws of the Province of Ontario, Canada, without regard to its conflict
          of law principles.
        </p>

        <h2 className="text-xl font-semibold mb-2">15. Contact Us</h2>
        <p className="mb-4">
          If you have any questions about these Terms, please contact us at:
          <br />
          <strong>Email:</strong> info@youwo.ai
        </p>

        <p className="italic text-sm">
          Disclaimer: These Terms of Service are for informational purposes only
          and do not constitute legal advice. Consult a legal professional to
          ensure compliance with all relevant laws and regulations.
        </p>
      </div>
      <Footer />
    </>
  );
};

export default TermsOfService;
